import React, { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Typography, Radio, RadioGroup, Box, FormControlLabel, Stack } from '@mui/material';
import { DEFAULT_PAYMENT } from '../../../constants';
import { useDictionary } from '../../../common/contexts/DictionaryContext';
import visaLogo from '../../../assets/images/logos/visa.svg';
import amexLogo from '../../../assets/images/logos/amex.svg';
import paypalLogo from '../../../assets/images/logos/paypal.svg';
import applePayLogo from '../../../assets/images/logos/applepay.svg';
import unzerLogo from '../../../assets/images/logos/unzer.svg';
import googlePayLogo from '../../../assets/images/logos/googlepay.svg';
import masterCardPayLogo from '../../../assets/images/logos/mastercard.svg';
import weroLogo from '../../../assets/images/logos/wero.svg';

const DEFAULT_PAYMENTS = [DEFAULT_PAYMENT, 9, 17, /*13,*/ 16, 18]; // unzer direct bank transfer currently not working
const ALL_PAYMENTS = [DEFAULT_PAYMENT, 9, 11, 17, /*13,*/ 16, 18, 10]; // unzer direct bank transfer currently not working

const logoMap = {
    6: [visaLogo, amexLogo, masterCardPayLogo], // creditCard
    7: [visaLogo, amexLogo, masterCardPayLogo], // creditCardOnline
    9: [paypalLogo], // paypal
    13: [unzerLogo], // unzerDirectBankTransfer
    14: [unzerLogo], // giropay (assuming same as unzer)
    15: [unzerLogo], // sofort (assuming same as unzer)
    16: [applePayLogo], // applepay
    17: [googlePayLogo], // googlepay
    18: [weroLogo], // wero
    11: [visaLogo, amexLogo, masterCardPayLogo], // prePaidByCreditCard
    12: [visaLogo, amexLogo, masterCardPayLogo], // creditCardViaPhone
};

const PaymentPart = ({ booking, onNumberChange, user, variant }) => {
    const { t } = useTranslation();

    const { paymentTypes = [] } = useDictionary();
    const paymentMap = new Map([...paymentTypes.map((item) => [item.id, item])]);

    const allowedPaymentTypes =
        user && user.allowedPaymentTypes && user.allowedPaymentTypes.length ? user.allowedPaymentTypes : null;

    const payments = allowedPaymentTypes
        ? ALL_PAYMENTS.filter((paymentId) => allowedPaymentTypes.includes(paymentId))
        : DEFAULT_PAYMENTS;

    const paymentLabel = (id) => {
        const payment = paymentMap.get(id);
        const fee = Number(payment.fee);
        const label = t(`payment.${payment.name}`);

        if (!fee) {
            return label;
        }

        return `${label} (${String(fee).replace(/\./g, ',')}%)`;
    };

    useEffect(() => {
        if (!payments.includes(booking.paymentTypeId)) {
            onNumberChange({ name: 'paymentTypeId', value: payments[0] });
        }
    }, [booking.paymentTypeId, onNumberChange, payments]);

    return (
        <>
            <Typography variant="h4" sx={{ mt: 3, mb: 3 }}>
                {t('choose-payment')}
            </Typography>
            <RadioGroup
                aria-label="payment types"
                name="paymentTypeId"
                value={booking.paymentTypeId}
                onChange={onNumberChange}
                row
            >
                {payments.map((id) => {
                    const logos = logoMap[id] || [];
                    const label = paymentLabel(id);
                    const checked = id === booking.paymentTypeId;

                    return (
                        <Box
                            key={id}
                            sx={{
                                width: 1,
                                border: '1px solid',
                                borderColor: checked ? 'primary.main' : 'grey.400',
                                borderRadius: 2,
                                px: 2.5,
                                mb: 1.75,
                                py: 1,
                                cursor: 'pointer',
                                backgroundColor: 'background.paper',
                                transition: 'border-color 0.3s',
                                '&:hover': {
                                    borderColor: 'primary.main',
                                },
                            }}
                        >
                            <FormControlLabel
                                sx={{ width: 1 }}
                                value={id}
                                control={<Radio slotProps={{ input: { autoComplete: 'off' } }} />}
                                disableTypography
                                label={
                                    <Stack
                                        direction="row"
                                        sx={{ width: 1, ml: 1, alignItems: 'center', justifyContent: 'space-between' }}
                                    >
                                        <Typography variant={variant}>{label}</Typography>
                                        <Stack sx={{ flexDirection: 'row' }}>
                                            {logos.map((logo, index) => (
                                                <Box
                                                    key={index}
                                                    component="img"
                                                    src={logo}
                                                    sx={{ height: { xs: 16, sm: 28 }, ml: 2 }}
                                                />
                                            ))}
                                        </Stack>
                                    </Stack>
                                }
                            />
                        </Box>
                    );
                })}
            </RadioGroup>
        </>
    );
};

export { PaymentPart };
